// Page-specific profile API calls — talks to lendcord-core directly (see
// webpack.config.js's devServer.proxy for /profile). Bearer-authenticated.
// Distinct from app/profile/api/profile.api.ts's getProfile (session-identity
// data shared with the app shell) — this file only owns concerns specific to
// the profile display page itself.

import { baseApi } from "@/app/store/baseApi";
import type { DocumentType, DocumentUploadUrlResponse, ProfileDocument } from "@/app/profile/profile.types";

export const profilePageApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Ownership-checked, short-lived presigned download URL for one document
    // — generated fresh per call, not bundled into GET /profile.
    // keepUnusedDataFor: 0 because a presigned URL expires — caching it past
    // the component that requested it would serve a dead link.
    getDocumentDownloadUrl: builder.query<{ url: string }, string>({
      query: (documentId) => ({ url: `/profile/documents/${documentId}/url`, auth: true }),
      keepUnusedDataFor: 0,
    }),

    // Step 1 of 3 for uploading a document — generates a storageKey and
    // presigned upload URL. Doesn't create a Document row yet (see
    // confirmDocumentUpload). No invalidation — nothing in GET /profile
    // changes until the upload is confirmed.
    requestDocumentUploadUrl: builder.mutation<DocumentUploadUrlResponse, DocumentType>({
      query: (type) => ({ url: "/profile/documents/upload-url", method: "POST", body: { type }, auth: true }),
    }),

    // Step 3 of 3 — confirms the upload landed and is the point the Document
    // row actually gets created (status starts PENDING).
    confirmDocumentUpload: builder.mutation<ProfileDocument, { type: DocumentType; storageKey: string }>({
      query: ({ type, storageKey }) => ({
        url: "/profile/documents",
        method: "POST",
        body: { type, storageKey },
        auth: true,
      }),
      invalidatesTags: ["Profile"],
    }),
  }),
});

export const {
  useGetDocumentDownloadUrlQuery,
  useLazyGetDocumentDownloadUrlQuery,
  useRequestDocumentUploadUrlMutation,
  useConfirmDocumentUploadMutation,
} = profilePageApi;

// Step 2 of 3 — PUT the raw file bytes to the presigned URL from step 1. This
// is deliberately NOT an RTK Query endpoint: the signature embedded in the
// URL is the auth — sending our own Bearer token or X-Tenant-Id here would be
// meaningless overhead at best, and it isn't a request to our own backend for
// baseApi's tenant/auth conventions to apply to.
//
// In local dev, API_URL is left empty (see .env.example) so every other
// fetch() call resolves to a relative path and rides webpack's dev proxy
// (webpack.config.js) instead of hitting lendcord-core cross-origin — that
// proxy is what let this rewrite to a bare path work, since there was no
// CORS_ALLOWED_ORIGINS support on the backend yet. lendcord-core's
// SecurityConfig now sets CORS headers on every response (including this
// stand-in's /internal/local-storage/** — see CORS_ALLOWED_ORIGINS), and a
// deployed build has no dev-server proxy to route a relative path to, so
// outside of dev this must stay the absolute presigned URL and rely on that
// CORS support instead. A real S3 presigned URL (once wired up) is also left
// absolute, same reasoning.
function toProxiedUploadUrl(uploadUrl: string): string {
  if (process.env.API_URL) {
    return uploadUrl;
  }
  const parsed = new URL(uploadUrl);
  return parsed.pathname.startsWith("/internal/local-storage") ? `${parsed.pathname}${parsed.search}` : uploadUrl;
}

export async function uploadDocumentFile(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(toProxiedUploadUrl(uploadUrl), {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Document upload failed with status ${response.status}`);
  }
}
